import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShiftPlan } from '../../../context/ShiftPlanContext';
import { useTeam } from '../../../context/TeamContext';
import { useAuth } from '../../../context/AuthContext';
import { TEAM_ROLES } from '../../../data/nurseAppModels';

const CreateShiftPage = () => {
  const navigate = useNavigate();
  const { createPlan } = useShiftPlan();
  const { currentTeam } = useTeam();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [planName, setPlanName] = useState('');

  // Check if user has permission
  const canCreatePlan = currentTeam && (
    currentTeam.userRole === TEAM_ROLES.LEADER || 
    currentTeam.userRole === TEAM_ROLES.DEPUTY_LEADER
  );

  // Generate available months (current month + next 11 months = 12 months total)
  const availableMonths = useMemo(() => {
    const months = [];
    const today = new Date();
    
    for (let i = 0; i < 12; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
      const year = date.getFullYear();
      const month = date.getMonth();
      const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
      
      // Thai month names
      const thaiMonths = [
        'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน',
        'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม',
        'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
      ];
      
      months.push({
        key: monthKey,
        year: year + 543, // Convert to Buddhist era
        month: month + 1,
        monthName: thaiMonths[month],
        displayName: `${thaiMonths[month]} ${year + 543}`,
        date: date
      });
    }
    
    return months;
  }, []);

  // Handle month toggle
  const handleMonthToggle = (month) => {
    setSelectedMonths(prev => {
      const exists = prev.find(m => m.key === month.key);
      if (exists) {
        return prev.filter(m => m.key !== month.key);
      } else {
        return [...prev, month];
      }
    });
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validation
      if (selectedMonths.length === 0) {
        throw new Error('กรุณาเลือกเดือนที่จะวางแผนอย่างน้อย 1 เดือน');
      }

      // Sort months by date
      const sortedMonths = [...selectedMonths].sort((a, b) => {
        return new Date(a.key + '-01') - new Date(b.key + '-01');
      });

      // Create plan for the first month (target month)
      const targetMonth = sortedMonths[0].key;
      
      // Calculate monthsAhead (how many months from first to last)
      const firstDate = new Date(sortedMonths[0].key + '-01');
      const lastDate = new Date(sortedMonths[sortedMonths.length - 1].key + '-01');
      const monthsAhead = Math.ceil(
        (lastDate.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24 * 30)
      ) + 1; // +1 to include the last month

      // Create plan data
      const planData = {
        targetMonth: targetMonth,
        monthsAhead: monthsAhead,
        planName: planName.trim() || null, // User-defined plan name
        planNumber: null, // Will be auto-generated
        version: 1, // Version 1 for first plan
        parentPlanId: null // No parent (first plan)
      };

      const result = await createPlan(planData);

      if (result.success) {
        // Show success message
        alert(`สร้างแผนเวรสำเร็จ!\nเลขแผน: ${result.plan?.planNumber}\nVersion: ${result.plan?.version}\n\nสมาชิกจะได้รับแจ้งเตือนให้กรอกเวร`);
        // Navigate to plans list
        navigate('/nurse/shifts/plans');
      } else if (result.error === 'EXISTING_PLAN') {
        // Show modal for existing plan
        const existingPlan = result.existingPlan;
        const shouldCreateVersion = window.confirm(
          `มีแผนเวรในเดือนนี้อยู่แล้ว!\n\n` +
          `แผนที่มีอยู่:\n` +
          `- ชื่อ: ${existingPlan.planName || existingPlan.planNumber}\n` +
          `- Version: ${existingPlan.version}\n` +
          `- สถานะ: ${existingPlan.status}\n\n` +
          `คุณต้องการ:\n` +
          `- กด "OK" เพื่อไปสร้าง Version ใหม่\n` +
          `- กด "Cancel" เพื่อยกเลิก`
        );
        
        if (shouldCreateVersion) {
          // Navigate to plans page and show create version modal
          navigate('/nurse/shifts/plans', {
            state: { 
              createVersionForPlanId: existingPlan.planId,
              createVersionForPlan: existingPlan
            }
          });
        }
      } else {
        setError(result.error || 'เกิดข้อผิดพลาดในการสร้างแผน');
      }
    } catch (err) {
      setError(err.message || 'เกิดข้อผิดพลาดในการสร้างแผน');
    } finally {
      setLoading(false);
    }
  };

  // If no team selected
  if (!currentTeam) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600 mb-4">กรุณาเลือกทีมก่อน</p>
          <button
            onClick={() => navigate('/nurse/teams')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            ไปเลือกทีม
          </button>
        </div>
      </div>
    );
  }

  // If no permission
  if (!canCreatePlan) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600 mb-4">คุณไม่มีสิทธิ์สร้างแผนเวร</p>
          <p className="text-sm text-gray-500 mb-4">เฉพาะหัวหน้าทีมและรองหัวหน้าทีมเท่านั้น</p>
          <button
            onClick={() => navigate('/nurse/shifts')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            กลับไปหน้าจัดการเวร
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/nurse/shifts')}
            className="text-blue-600 hover:text-blue-700 mb-4 flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            กลับไปหน้าจัดการเวร
          </button>
          <h1 className="text-2xl font-bold text-gray-800">สร้างเวรใหม่</h1>
        </div>

        {/* Team Info Card */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">ข้อมูลทีม</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">แผนก</p>
              <p className="font-medium text-gray-800">{currentTeam.department || 'ยังไม่ระบุ'}</p>
            </div>
            {currentTeam.hospital && (
              <div>
                <p className="text-sm text-gray-600">โรงพยาบาล</p>
                <p className="font-medium text-gray-800">{currentTeam.hospital}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-600">จำนวนสมาชิก</p>
              <p className="font-medium text-gray-800">{currentTeam.members?.length || 0} คน</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">หัวหน้าทีม</p>
              <p className="font-medium text-gray-800">{currentTeam.leaderName || 'ยังไม่ระบุ'}</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Plan Name */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ชื่อแผน <span className="text-gray-500">(ไม่บังคับ - ถ้าไม่กรอกจะใช้ชื่ออัตโนมัติ)</span>
            </label>
            <input
              type="text"
              value={planName}
              onChange={(e) => setPlanName(e.target.value)}
              placeholder="เช่น แผนเวรเดือนธันวาคม 2568"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              ชื่อแผนจะช่วยให้จำง่ายและแยกแยะแผนต่างๆ ได้ชัดเจน
            </p>
          </div>

          {/* Month Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-4">
              เลือกเดือนที่จะวางแผน * <span className="text-gray-500">(สามารถเลือกหลายเดือนได้)</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {availableMonths.map((month) => {
                const isSelected = selectedMonths.some(m => m.key === month.key);
                return (
                  <button
                    key={month.key}
                    type="button"
                    onClick={() => handleMonthToggle(month)}
                    className={`
                      px-4 py-3 rounded-lg border-2 transition-all duration-200 text-left
                      ${isSelected
                        ? 'border-blue-500 bg-blue-50 shadow-md'
                        : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                      }
                    `}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className={`
                          font-semibold text-sm
                          ${isSelected ? 'text-blue-700' : 'text-gray-700'}
                        `}>
                          {month.monthName}
                        </div>
                        <div className={`
                          text-xs mt-0.5
                          ${isSelected ? 'text-blue-600' : 'text-gray-500'}
                        `}>
                          {month.year}
                        </div>
                      </div>
                      {isSelected && (
                        <div className="ml-2">
                          <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            {selectedMonths.length === 0 && (
              <p className="text-sm text-gray-500 mt-2">กรุณาเลือกเดือนที่จะวางแผนอย่างน้อย 1 เดือน</p>
            )}
            {selectedMonths.length > 0 && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm font-medium text-blue-900 mb-2">
                  เดือนที่เลือกแล้ว ({selectedMonths.length} เดือน):
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedMonths
                    .sort((a, b) => new Date(a.key + '-01') - new Date(b.key + '-01'))
                    .map(month => (
                      <span
                        key={month.key}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700"
                      >
                        {month.monthName} {month.year}
                      </span>
                    ))}
                </div>
              </div>
            )}
          </div>

          {/* Info Section */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h3 className="font-medium text-blue-900 mb-2">ℹ️ ข้อมูลเพิ่มเติม</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• เลขแผน (Plan Number) จะถูกสร้างอัตโนมัติเป็น index</li>
              <li>• Version จะเริ่มต้นที่ Version 1 สำหรับแผนครั้งแรก</li>
              <li>• สามารถสร้าง Version ใหม่ได้เมื่อมีการปรับแผน</li>
              <li>• สมาชิกจะได้รับแจ้งเตือนเมื่อสร้างแผนเสร็จ</li>
              <li>• สมาชิกสามารถกรอกเวรที่ต้องการได้ตาม calendar</li>
            </ul>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate('/nurse/shifts')}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              disabled={loading || selectedMonths.length === 0}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {loading ? 'กำลังสร้างแผน...' : 'สร้างแผนเวร'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateShiftPage;



